import { IRequestContext, IRequestContextUser } from '@msdyn365-commerce/core-internal';
import { Channel, User, UserFactory } from '@relewise/client';
import {
    checkIfServiceConsentIsGiven,
    CLIENT_COOKIE_NAME,
    getCookieValueFromRequest,
    hasUCUILoaded
} from '../../../shared/functions/cookie';

export const TEMPORARY_USER_ID_COOKIE_NAME = 'relewise-temporary-user-id';
const DEVICE_COOKIE_VALUE_FOR_MOBILE_APP = 'webview';

const APP_CLIENT_NAME = 'App';

export class RelewiseUserService {
    protected request: IRequestContext;
    protected authUser: IRequestContextUser | undefined;
    protected userAgent: string | undefined;
    protected countryIsoCode: string | null;

    constructor(request: IRequestContext) {
        this.request = request;
        this.authUser = request.user;
        this.countryIsoCode = request.channel?.ChannelCountryRegionISOCode ?? null;
        this.userAgent = typeof navigator === 'undefined' ? request.headers['user-agent'] : navigator.userAgent;
    }

    private async getTemporaryUserId() {
        return typeof window === 'undefined'
            ? getCookieValueFromRequest(TEMPORARY_USER_ID_COOKIE_NAME, this.request)
            : this.getUsercentricsControllerId();
    }

    private async getUsercentricsControllerId() {
        if (!hasUCUILoaded(window)) {
            return;
        }
        const hasConsented = await checkIfServiceConsentIsGiven('Relewise');
        if (!hasConsented) {
            return;
        }
        return window.UC_UI.getControllerId();
    }

    private async buildUnauthenticatedUser() {
        const temporaryId = await this.getTemporaryUserId();

        return !!temporaryId ? UserFactory.byTemporaryId(temporaryId) : UserFactory.anonymous();
    }

    private async buildAuthenticatedUser() {
        const { isAuthenticated, customerAccountNumber, emailAddress } = this.authUser ?? {};
        if (!isAuthenticated || !customerAccountNumber || !emailAddress) {
            return;
        }

        const temporaryId = await this.getTemporaryUserId();
        return UserFactory.byAuthenticatedId(customerAccountNumber, temporaryId, {
            email: emailAddress
        });
    }

    private getDeviceType() {
        const ua = this.userAgent;
        if (!ua) {
            return 'Unknown';
        }

        // iPad needs a special check: it sometimes pretends to be a mac when used in desktop mode
        if (/iPad/.test(ua) || (/Mac/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1)) {
            return 'Tablet';
        }
        if (/Android/.test(ua) && !/Mobile/.test(ua)) {
            return 'Tablet';
        }
        if (/Android|iPhone/.test(ua)) {
            return 'Phone';
        }

        return 'Desktop';
    }

    private getPlatform() {
        const ua = this.userAgent;
        if (!ua) {
            return 'Unknown';
        }

        // MUST check for iPad before checking for Mac! iPad UA can have the word 'Mac' in it as well.
        if (/iPhone|iPad/i.test(ua)) {
            return 'iOS';
        }
        if (/Mac/i.test(ua)) {
            return 'macOS';
        }
        if (/android/i.test(ua)) {
            return 'Android';
        }
        if (/Windows/i.test(ua)) {
            return 'Windows';
        }
        if (/Linux/i.test(ua)) {
            return 'Linux';
        }

        return 'Unknown';
    }

    private getUserChannel(): Channel | undefined {
        const deviceType = this.getDeviceType();
        const platform = this.getPlatform();
        // Name of the browser, or 'webview' when in mobile app
        const clientCookieValue = getCookieValueFromRequest(CLIENT_COOKIE_NAME, this.request);
        const clientName = clientCookieValue === DEVICE_COOKIE_VALUE_FOR_MOBILE_APP ? APP_CLIENT_NAME : clientCookieValue;

        return {
            name: deviceType,
            subChannel: {
                name: platform,
                ...(clientName
                    ? {
                          subChannel: {
                              name: clientName
                          }
                      }
                    : {})
            }
        };
    }

    private getUserClassifications(): Record<string, string | null> | undefined {
        return {
            country: this.countryIsoCode
        };
    }

    public async getUser() {
        let user: User;

        const authenticatedUser = await this.buildAuthenticatedUser();
        if (authenticatedUser) {
            user = authenticatedUser;
        } else {
            user = await this.buildUnauthenticatedUser();
        }

        user.channel = this.getUserChannel();
        user.classifications = this.getUserClassifications();

        return user;
    }
}
